import React from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Paper,
  Button,
} from '@mui/material'
import { Candidate, ApplicationMetaData } from './types'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import moment from 'moment-timezone'
import { styled } from '@mui/material/styles'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import MatchScoreDialog from './MatchScoreDialog'

const getCandidateJobSpecificUrl = (candidateId: string, jobId: string) => {
  // Use environment-specific logic here if needed
  return `https://partner.assembly-industries.com/candidate/${candidateId}?jid=${jobId}`
}

const SectionLabel = styled(Typography)(() => ({
  fontSize: '20px',
  marginBottom: '10px',
  fontWeight: 500,
  marginTop: '20px',
}))

const QuestionAndAnswerBox = styled(Box)(() => ({
  marginBottom: '10px',
}))

const Question = styled(Typography)(() => ({
  marginBottom: '5px',
}))

const ScoreDetails = styled(Typography)(() => ({
  whiteSpace: 'pre-line',
  marginTop: '8px',
  padding: '12px',
  backgroundColor: '#f5f5f5',
  borderRadius: '4px',
  fontSize: '14px',
}))

const ScoreValue = styled(Typography)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '4px 8px',
  borderRadius: '16px',
  backgroundColor: theme.palette.primary.light,
  color: theme.palette.primary.contrastText,
  fontWeight: 'bold',
  marginLeft: '8px',
}))

type EditCandidateDialogProps = {
  open: boolean
  onClose: () => void
  candidate: Candidate | null
  onSuccess?: (candidate: Candidate) => void
}

export default function ApplicationsDialog({
  open,
  onClose,
  candidate,
  onSuccess,
}: EditCandidateDialogProps) {
  const [matchScoreDialogOpen, setMatchScoreDialogOpen] = React.useState(false)
  const [selectedApplicationIndex, setSelectedApplicationIndex] = React.useState<number>(-1)
  const [localCandidate, setLocalCandidate] = React.useState<Candidate | null>(null)

  // Update local candidate when prop changes
  React.useEffect(() => {
    setLocalCandidate(candidate)
  }, [candidate])

  const handleOpenMatchScoreDialog = (index: number) => {
    setSelectedApplicationIndex(index)
    setMatchScoreDialogOpen(true)
  }

  const handleCloseMatchScoreDialog = () => {
    setMatchScoreDialogOpen(false)
    setSelectedApplicationIndex(-1)
  }

  const handleMatchScoreSuccess = (updatedCandidate: Candidate) => {
    if (onSuccess) {
      onSuccess(updatedCandidate)
    }
    // Update local state with the new candidate data
    setLocalCandidate(updatedCandidate)
    // Only close the match score dialog, keep the main applications dialog open
    setMatchScoreDialogOpen(false)
    setSelectedApplicationIndex(-1)
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>
          Applications ({localCandidate?.firstName} {localCandidate?.lastName})
          {onClose ? (
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          ) : null}
        </DialogTitle>
        <DialogContent dividers>
          {localCandidate?.applicationMetaData.map((application, index) => (
            <Accordion key={index}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
              >
                <Typography variant="h6">{application.jobTitle}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
                  <b>Apply Date:</b>{' '}
                  {moment(application.applyDate).format('DD/MM/YYYY')}
                </Typography>
                <Typography>
                  <b>External Job Board Candidate Link:</b>{' '}
                  <a
                    href={application.externalJobBoardCandidateLink}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Link
                  </a>
                </Typography>
                <Typography>
                  <b>External Job Board Id :</b> {application.externalJobBoardId}
                </Typography>
                <Typography>
                  <b>Internal Job Id :</b> {application.jobID}
                </Typography>
                <Typography>
                  <b>Candidate Job Specific Profile Link :</b>{' '}
                  <a
                    href={getCandidateJobSpecificUrl(localCandidate?.id || '', application.jobID)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Link
                  </a>
                </Typography>
                <SectionLabel>Questionaire</SectionLabel>
                {Object.entries(application.questionnaire).map(
                  ([key, value], index) => (
                    <QuestionAndAnswerBox key={index}>
                      <Question>
                        <b>
                          {index + 1}. {key}
                        </b>
                      </Question>
                      <Typography>
                        <b>Answer:</b> {`${value}`}
                      </Typography>
                    </QuestionAndAnswerBox>
                  )
                )}
                <SectionLabel>Match Score</SectionLabel>
                <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: '#f8f8f8' }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center">
                      <Typography variant="subtitle1" fontWeight="bold">
                        Match Score:
                      </Typography>
                      <ScoreValue>
                        {application.matchScore}
                      </ScoreValue>
                    </Box>
                    <Button
                      startIcon={<EditIcon />}
                      onClick={() => handleOpenMatchScoreDialog(index)}
                      size="small"
                    >
                      Edit
                    </Button>
                  </Box>
                  {application.matchScoreDetails && (
                    <Box mt={1}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Details:
                      </Typography>
                      <ScoreDetails>
                        {application.matchScoreDetails}
                      </ScoreDetails>
                    </Box>
                  )}
                </Paper>
              </AccordionDetails>
            </Accordion>
          ))}
        </DialogContent>
      </Dialog>
      <MatchScoreDialog
        open={matchScoreDialogOpen}
        onClose={handleCloseMatchScoreDialog}
        candidate={localCandidate}
        applicationIndex={selectedApplicationIndex}
        onSuccess={handleMatchScoreSuccess}
      />
    </>
  )
}
